// LEAVECAL: ONE calendar component, four configurations.
//
// The three calendars the task asks for (Holidays, Sick leave, Working from home) and the global
// view are the SAME component with a different `types` prop, not four files. Four files is how this
// codebase ended up with five divergent shells, and the UX audit's central finding was exactly
// that; this is the shape the audit recommended.
//
//   <LeaveCalendar types={['holiday']} />            Holidays
//   <LeaveCalendar types={['sick']} />               Sick leave
//   <LeaveCalendar types={['wfh']} showWfhCounts />  Working from home
//   <LeaveCalendar global />                         all three together, with the legend
//
// BUILT ON theme.js TOKENS ONLY. Every colour, size, radius and space below resolves from
// tokens(dark) / TYPE / S / RADIUS, and no hex literal appears in this file except the four
// absence-type accents, which are new semantic tokens defined once at the top and nowhere else.
//
// THE SERVER DECIDES WHAT IS VISIBLE. This component renders what GET /leave/calendar returns and
// makes no visibility decision of its own: it never filters by employee, never asks for a type the
// user did not choose, and treats a 403 as "not for you" rather than an error. The type it shows is
// the type the API sent, so a masked "absent" row renders as Absent because that is what arrived.
//
// TYPE IS NEVER CONVEYED BY COLOUR ALONE (point 2c and WCAG 1.4.1): every entry carries a text
// label AND a short letter badge, and the legend repeats both. A colour-blind reader, and a reader
// on a monochrome screen, both still know which absence they are looking at.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { TYPE, S, RADIUS, tokens } from './theme';
import { Btn, LoadError, ScrollableTable, useIsNarrow } from './ui';
import CertificateUpload from './CertificateUpload';

const API = process.env.REACT_APP_API_URL || '/api';

// The four absence accents. ONE definition, used by the cells and by the legend, so a colour and
// its label can never disagree. Chosen for contrast against both surfaces rather than for variety:
// each is readable as text on its own tint, which is what the pill below renders.
// ABSREG: exported so the absence register renders a type with the SAME colour this calendar
// does. Exported rather than copied precisely so the two screens cannot drift: a second literal
// would be a second definition of "what colour is sick leave".
export const ABSENCE_ACCENT = {
  holiday: { fg: '#1e5fa8', bg: '#e8f1fb', letter: 'H' },
  sick:    { fg: '#9a3412', bg: '#fdeee6', letter: 'S' },
  wfh:     { fg: '#1e7e45', bg: '#e9f7ef', letter: 'W' },
  absent:  { fg: '#4b5563', bg: '#f1f2f4', letter: 'A' },
};
const ACCENT = ABSENCE_ACCENT;

const ALL_TYPES = ['holiday', 'sick', 'wfh'];

// ── Date helpers. PURE LOCAL DATE ARITHMETIC, NO Date PARSING OF AN ISO STRING. ──
//
// `new Date('2026-10-25')` is parsed as UTC MIDNIGHT and then rendered in the local zone, which in
// any zone behind UTC shows the previous day. A leave date is a whole day (point 2f), so every
// comparison here is done on the 'YYYY-MM-DD' STRING, and the only Date objects built are from
// explicit (y, m, d) parts, which the constructor treats as local.
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fromParts = (y, m, d) => new Date(y, m, d);
const addDays = (d, n) => fromParts(d.getFullYear(), d.getMonth(), d.getDate() + n);
// Monday-first week start, matching the UK working week the business rules are stated in.
const startOfWeek = (d) => addDays(d, -((d.getDay() + 6) % 7));
const startOfMonth = (d) => fromParts(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => fromParts(d.getFullYear(), d.getMonth() + 1, 0);

/** Does an entry (ISO start/end, inclusive) cover this ISO day? String comparison, no Date. */
const covers = (entry, dayIso) => entry.start_date <= dayIso && entry.end_date >= dayIso;

export default function LeaveCalendar({
  types = null,          // null + global=false means "all three", same as the global view's data
  global = false,        // renders the legend and the type filter
  showWfhCounts = false, // the per-employee WFH table (HR / COO / Country Lead see it populated)
  dark = false,
}) {
  const { t } = useTranslation();
  const tk = tokens(dark);
  const isNarrow = useIsNarrow();

  const [view, setView] = useState('month');          // 'month' | 'week'
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return fromParts(now.getFullYear(), now.getMonth(), now.getDate());
  });
  const [typeFilter, setTypeFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');

  const [data, setData] = useState(null);
  const [wfhCounts, setWfhCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  // The window the API is asked for. A month view asks for the whole visible grid (which spills
  // into the neighbouring months), so an absence starting in the previous month still renders.
  const [rangeStart, rangeEnd] = useMemo(() => {
    if (view === 'week') {
      const s = startOfWeek(cursor);
      return [s, addDays(s, 6)];
    }
    const s = startOfWeek(startOfMonth(cursor));
    const e = addDays(startOfWeek(endOfMonth(cursor)), 6);
    return [s, e];
  }, [view, cursor]);

  const wantedTypes = useMemo(() => {
    const base = types && types.length ? types : ALL_TYPES;
    if (typeFilter !== 'all' && base.includes(typeFilter)) return [typeFilter];
    return base;
  }, [types, typeFilter]);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    setForbidden(false);
    const params = {
      start: iso(rangeStart),
      end: iso(rangeEnd),
      types: wantedTypes.join(','),
    };
    if (entityFilter !== 'all') params.entity = entityFilter;
    axios.get(`${API}/leave/calendar`, { params })
      .then(res => { setData(res.data); setLoading(false); })
      .catch(err => {
        // A 403 is "this view is not for you", which is a correct and expected answer for most
        // accounts - not a failure to report as an error. Anything else IS an error, and is shown
        // as one rather than as an empty calendar (the audit's empty-versus-error finding).
        if (err?.response?.status === 403) { setForbidden(true); setLoading(false); return; }
        setError(true);
        setLoading(false);
      });
  }, [rangeStart, rangeEnd, wantedTypes, entityFilter]);

  useEffect(() => { load(); }, [load]);

  // The per-employee WFH counts for the displayed month. Only requested when the caller asked for
  // them; an ordinary employee gets exactly their own row back, which is their own data.
  useEffect(() => {
    if (!showWfhCounts) return;
    let live = true;
    axios.get(`${API}/leave/wfh-counts`,
      { params: { year: cursor.getFullYear(), month: cursor.getMonth() + 1 } })
      .then(res => { if (live) setWfhCounts(Array.isArray(res.data) ? res.data : []); })
      // A failed counts read must not blank the calendar above it, which still rendered fine.
      .catch(() => { if (live) setWfhCounts([]); });
    return () => { live = false; };
  }, [showWfhCounts, cursor]);

  // MEDLINK: memoised because two useMemo hooks below depend on it. A bare `data?.entries || []`
  // produces a NEW empty array on every render when there are no entries, which would make those
  // hooks recompute every time and which react-hooks/exhaustive-deps correctly warns about.
  const entries = useMemo(() => data?.entries || [], [data]);

  // MEDLINK: the viewer's OWN sick rows that carry certificate state. The server only attaches it
  // to those, so this filter cannot widen the set - it selects from what was already scoped.
  // `required` and `missing` are the states worth a banner; an absence already covered, or one
  // that needs no certificate, is not surfaced here.
  const myCertificateRows = useMemo(
    () => entries.filter(e => e.certificate_status === 'required'
                           || e.certificate_status === 'missing'),
    [entries],
  );
  const bankHolidays = useMemo(
    () => new Map((data?.bank_holidays || []).map(h => [h.date, h])),
    [data]
  );

  const entities = useMemo(() => {
    const s = new Set();
    entries.forEach(e => { if (e.entity) s.add(e.entity); });
    return Array.from(s).sort();
  }, [entries]);

  // ── Navigation. Keyboard reachable because these are real <button> elements (Btn). ──
  const step = (dir) => {
    if (view === 'week') { setCursor(addDays(cursor, 7 * dir)); return; }
    setCursor(fromParts(cursor.getFullYear(), cursor.getMonth() + dir, 1));
  };
  const goToday = () => {
    const now = new Date();
    setCursor(fromParts(now.getFullYear(), now.getMonth(), now.getDate()));
  };

  const heading = useMemo(() => {
    const lang = (typeof navigator !== 'undefined' && navigator.language) || 'en-GB';
    if (view === 'week') {
      return t('leaveCalendar.weekOf', {
        date: startOfWeek(cursor).toLocaleDateString(lang, { day: 'numeric', month: 'long', year: 'numeric' }),
      });
    }
    return cursor.toLocaleDateString(lang, { month: 'long', year: 'numeric' });
  }, [view, cursor, t]);

  const typeLabel = (type) => ({
    holiday: t('leaveCalendar.typeHoliday'),
    sick: t('leaveCalendar.typeSick'),
    wfh: t('leaveCalendar.typeWfh'),
    absent: t('leaveCalendar.typeAbsent'),
  }[type] || type);

  const s = {
    page: { fontFamily: 'inherit', color: tk.tx },
    bar: {
      display: 'flex', flexWrap: 'wrap', gap: S[2], alignItems: 'center',
      marginBottom: S[4],
    },
    heading: { ...TYPE.h2, color: tk.tx, margin: 0, flex: '1 1 auto', minWidth: 0 },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
      gap: '1px',
      backgroundColor: tk.line,
      border: `1px solid ${tk.line}`,
      borderRadius: RADIUS.lg,
      overflow: 'hidden',
    },
    dowCell: {
      ...TYPE.label, color: tk.tx3, backgroundColor: tk.bg,
      padding: `${S[2]} ${S[1]}`, textAlign: 'center',
    },
    dayCell: (inMonth, isToday) => ({
      backgroundColor: tk.surface,
      minHeight: isNarrow ? '58px' : '96px',
      padding: S[1],
      opacity: inMonth ? 1 : 0.45,
      boxShadow: isToday ? `inset 0 0 0 2px ${tk.navy}` : 'none',
    }),
    dayNum: { ...TYPE.meta, color: tk.tx3, display: 'block', marginBottom: '2px' },
    pill: (type) => ({
      display: 'flex', alignItems: 'center', gap: '4px',
      backgroundColor: ACCENT[type]?.bg || tk.hover,
      color: ACCENT[type]?.fg || tk.tx2,
      borderRadius: RADIUS.sm,
      padding: '1px 4px',
      fontSize: '11px', lineHeight: 1.3,
      marginBottom: '2px',
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    }),
    letter: {
      fontWeight: 700, fontSize: '10px', flexShrink: 0,
      border: '1px solid currentColor', borderRadius: RADIUS.sm,
      padding: '0 2px',
    },
    bh: {
      ...TYPE.meta, color: tk.tx3, fontStyle: 'italic',
      display: 'block', marginBottom: '2px', fontSize: '10px',
    },
    certBanner: {
      display: 'flex', flexDirection: 'column', gap: S[2],
      marginBottom: S[3], padding: S[3], borderRadius: RADIUS.md,
      backgroundColor: tk.sf2, border: `1px solid ${tk.bd}`,
    },
    legend: {
      display: 'flex', flexWrap: 'wrap', gap: S[3], alignItems: 'center',
      marginBottom: S[3], ...TYPE.meta, color: tk.tx2,
    },
    select: {
      padding: '6px 10px', border: `1px solid ${tk.border}`, borderRadius: RADIUS.md,
      fontSize: '13px', backgroundColor: tk.surface, color: tk.tx, fontFamily: 'inherit',
    },
    card: {
      backgroundColor: tk.surface, border: `1px solid ${tk.line}`,
      borderRadius: RADIUS.xl, padding: S[5], marginTop: S[4],
    },
    th: { ...TYPE.label, color: tk.tx3, textAlign: 'left', padding: `${S[2]} ${S[3]}`,
          borderBottom: `1px solid ${tk.line}` },
    td: { ...TYPE.body, color: tk.tx, padding: `${S[2]} ${S[3]}`,
          borderBottom: `1px solid ${tk.lineSoft}` },
    empty: { ...TYPE.meta, color: tk.tx3, margin: `${S[2]} 0 0 0` },
    over: {
      backgroundColor: tk.warnBg, color: tk.warnFg,
      border: `1px solid ${tk.warnFg}`, borderRadius: RADIUS.md,
      padding: `${S[2]} ${S[3]}`, ...TYPE.meta, marginBottom: S[3],
    },
  };

  const days = useMemo(() => {
    const out = [];
    let d = rangeStart;
    while (iso(d) <= iso(rangeEnd)) { out.push(d); d = addDays(d, 1); }
    return out;
  }, [rangeStart, rangeEnd]);

  const todayIso = iso(new Date());
  const dow = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

  if (forbidden) {
    // Not an error state: this account simply does not have this view.
    return <p style={s.empty}>{t('leaveCalendar.noAbsences')}</p>;
  }

  return (
    <div style={s.page}>
      <div style={s.bar}>
        <h2 style={s.heading}>{heading}</h2>
        <Btn variant="secondary" dark={dark} onClick={() => step(-1)}>
          {t('leaveCalendar.previous')}
        </Btn>
        <Btn variant="secondary" dark={dark} onClick={goToday}>
          {t('leaveCalendar.today')}
        </Btn>
        <Btn variant="secondary" dark={dark} onClick={() => step(1)}>
          {t('leaveCalendar.next')}
        </Btn>
        <Btn variant={view === 'month' ? 'primary' : 'ghost'} dark={dark}
             onClick={() => setView('month')}>
          {t('leaveCalendar.month')}
        </Btn>
        <Btn variant={view === 'week' ? 'primary' : 'ghost'} dark={dark}
             onClick={() => setView('week')}>
          {t('leaveCalendar.week')}
        </Btn>
      </div>

      <div style={s.bar}>
        {global && (
          <label style={{ ...TYPE.meta, color: tk.tx2 }}>
            {t('leaveCalendar.filterType')}{' '}
            <select style={s.select} value={typeFilter}
                    onChange={e => setTypeFilter(e.target.value)}
                    aria-label={t('leaveCalendar.filterType')}>
              <option value="all">{t('leaveCalendar.allTypes')}</option>
              {ALL_TYPES.map(ty => (
                <option key={ty} value={ty}>{typeLabel(ty)}</option>
              ))}
            </select>
          </label>
        )}
        {/* The entity filter is offered ONLY when the server says this account sees more than one
            entity (point 2b). can_see_all_employees comes from the API, so the UI never offers a
            filter that would return nothing. */}
        {data?.can_see_all_employees && entities.length > 1 && (
          <label style={{ ...TYPE.meta, color: tk.tx2 }}>
            {t('leaveCalendar.filterEntity')}{' '}
            <select style={s.select} value={entityFilter}
                    onChange={e => setEntityFilter(e.target.value)}
                    aria-label={t('leaveCalendar.filterEntity')}>
              <option value="all">{t('leaveCalendar.allEntities')}</option>
              {entities.map(en => <option key={en} value={en}>{en}</option>)}
            </select>
          </label>
        )}
      </div>

      {global && (
        <div style={s.legend} aria-label={t('leaveCalendar.legend')}>
          <strong>{t('leaveCalendar.legend')}:</strong>
          {ALL_TYPES.map(ty => (
            <span key={ty} style={s.pill(ty)}>
              <span style={s.letter} aria-hidden="true">{ACCENT[ty].letter}</span>
              {typeLabel(ty)}
            </span>
          ))}
          <span style={s.pill('absent')}>
            <span style={s.letter} aria-hidden="true">{ACCENT.absent.letter}</span>
            {typeLabel('absent')}
          </span>
          <span style={{ ...TYPE.meta, color: tk.tx3, fontStyle: 'italic' }}>
            {t('leaveCalendar.bankHoliday')}
          </span>
        </div>
      )}

      {error && <LoadError dark={dark} message={t('leaveCalendar.loadError')} onRetry={load} />}

      {/* MEDLINK point 2: the certificate action on the CALENDAR too.
          A SUMMARY ABOVE THE GRID rather than a control inside a day cell, deliberately: an
          absence spans many cells, so a per-cell control would repeat the same upload button once
          per day of the same sick leave. The server sends certificate state for the viewer's OWN
          sick rows only, so a row belonging to anybody else cannot appear here whatever this
          component does. */}
      {myCertificateRows.length > 0 && (
        <div style={s.certBanner} aria-label={t('certificate.uploadAction')}>
          {myCertificateRows.map(e => (
            <CertificateUpload
              key={e.id}
              declaration={{ ...e, leave_type: 'sick' }}
              employeeId={e.employee_id}
              dark={dark}
              onUploaded={load}
            />
          ))}
        </div>
      )}

      {!error && (
        <div style={s.grid} role="grid" aria-label={t('leaveCalendar.title')}>
          {dow.map(d => (
            <div key={d} style={s.dowCell} role="columnheader">{t(`leaveCalendar.${d}`)}</div>
          ))}
          {days.map(d => {
            const dIso = iso(d);
            const inMonth = view === 'week' || d.getMonth() === cursor.getMonth();
            const bh = bankHolidays.get(dIso);
            const dayEntries = entries.filter(e => covers(e, dIso));
            return (
              <div key={dIso} style={s.dayCell(inMonth, dIso === todayIso)} role="gridcell">
                <span style={s.dayNum}>{d.getDate()}</span>
                {/* Point 2e: labelled, so a reader outside the UK does not take it for a company
                    closure. Italic plus the words, never a colour alone. */}
                {bh && <span style={s.bh}>{t('leaveCalendar.bankHoliday')}</span>}
                {dayEntries.map(e => (
                  <span key={e.id} style={s.pill(e.leave_type)}
                        title={`${typeLabel(e.leave_type)}: ${e.employee_name || ''}`}>
                    <span style={s.letter} aria-hidden="true">
                      {(ACCENT[e.leave_type] || ACCENT.absent).letter}
                    </span>
                    {/* The TYPE IN WORDS as well as the colour and the letter, so the type is
                        never conveyed by colour only. The name follows when the viewer is seeing
                        more than their own rows. */}
                    <span>
                      {typeLabel(e.leave_type)}
                      {e.employee_name ? ` ${e.employee_name}` : ''}
                      {e.approval_status === 'pending' ? ` (${t('leaveCalendar.pending')})` : ''}
                    </span>
                  </span>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {!error && !loading && entries.length === 0 && (
        <p style={s.empty}>{t('leaveCalendar.noAbsences')}</p>
      )}

      {showWfhCounts && (
        <div style={s.card}>
          <h3 style={{ ...TYPE.h3, color: tk.tx, margin: `0 0 ${S[3]} 0` }}>
            {t('leaveCalendar.wfhCountsTitle')}
          </h3>
          {wfhCounts.length === 0 ? (
            <p style={s.empty}>{t('leaveCalendar.wfhCountsEmpty')}</p>
          ) : (
            <ScrollableTable>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={s.th}>{t('leaveCalendar.employee')}</th>
                    <th style={s.th}>{t('leaveCalendar.filterEntity')}</th>
                    <th style={s.th}>{t('leaveCalendar.days')}</th>
                  </tr>
                </thead>
                <tbody>
                  {wfhCounts.map(r => (
                    <tr key={r.employee_id}>
                      <td style={s.td}>{r.employee_name || '-'}</td>
                      <td style={s.td}>{r.entity || '-'}</td>
                      <td style={s.td}>
                        {/* Over the limit is stated IN WORDS as well as styled, so the warning
                            does not depend on colour. */}
                        {r.over_limit
                          ? <strong style={{ color: tk.warnFg }}>
                              {t('leaveCalendar.wfhThisMonth', { used: r.used, limit: r.limit })}
                            </strong>
                          : t('leaveCalendar.wfhThisMonth', { used: r.used, limit: r.limit })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollableTable>
          )}
        </div>
      )}
    </div>
  );
}

/** The employee's own counter, for the leave form. Point 4b's first half.
 *
 * Separate from the calendar because the FORM needs it before anything is declared, and a form
 * should not have to mount a calendar to learn a number.
 */
export function WfhMonthCounter({ year, month, dark = false, style }) {
  const { t } = useTranslation();
  const tk = tokens(dark);
  const [st, setSt] = useState(null);

  useEffect(() => {
    let live = true;
    const now = new Date();
    const y = year || now.getFullYear();
    const m = month || now.getMonth() + 1;
    axios.get(`${API}/leave/wfh-count`, { params: { year: y, month: m } })
      .then(res => { if (live) setSt(res.data); })
      .catch(() => { if (live) setSt(null); });
    return () => { live = false; };
  }, [year, month]);

  if (!st) return null;
  return (
    <div style={{ ...TYPE.meta, color: st.over_limit ? tk.warnFg : tk.tx2, ...(style || {}) }}>
      <strong>{t('leaveCalendar.wfhThisMonth', { used: st.used, limit: st.limit })}</strong>
      {st.over_limit && (
        <div style={{ marginTop: '4px' }}>
          {t('leaveCalendar.wfhOverLimit', { limit: st.limit })}
        </div>
      )}
    </div>
  );
}
